// file: pharmakon/client/src/services/adminApi.js
/**
 * @fileoverview PHARMAKON — Admin API Client (Module 1)
 * @module services/adminApi
 *
 * Uses the shared Axios instance from api.client.js.
 * The interceptor already unwraps response.data, so all functions
 * return the payload object directly (not the Axios response).
 *
 * Routes served by: server/src/routes/admin.routes.js
 */

import api from "./api.client.js";

// ── Platform KPIs ──────────────────────────────────────────────────────────────
export const getKPIs = () => api.get("/admin/kpis");
export const getChargilyBalance = () => api.get("/admin/balance");

// ── Pharmacies ─────────────────────────────────────────────────────────────────
export const listPharmacies = (p) =>
  api.get("/admin/pharmacies", { params: p });
export const getPharmacieDetail = (id) => api.get(`/admin/pharmacies/${id}`);
export const updateStatut = (id, body) =>
  api.patch(`/admin/pharmacies/${id}/statut`, body);

// ── Fournisseurs ───────────────────────────────────────────────────────────────
export const listFournisseurs = (p) =>
  api.get("/admin/fournisseurs", { params: p });
export const getFournisseurDetail = (id) =>
  api.get(`/admin/fournisseurs/${id}`);
export const updateFournisseurStatut = (id, body) =>
  api.patch(`/admin/fournisseurs/${id}/statut`, body);

// ── Laboratoires ───────────────────────────────────────────────────────────────
export const listLaboratoires = (p) =>
  api.get("/admin/laboratoires", { params: p });
export const getLaboratoireDetail = (id) =>
  api.get(`/admin/laboratoires/${id}`);
export const updateLaboratoireStatut = (id, body) =>
  api.patch(`/admin/laboratoires/${id}/statut`, body);

// ── Licences ───────────────────────────────────────────────────────────────────
export const getLicences = (pharmacie_id) =>
  api.get(`/admin/pharmacies/${pharmacie_id}/licences`);
export const overrideLicence = (id, body) =>
  api.patch(`/admin/licences/${id}`, body);
export const toggleModule = (post_licence_id, body) =>
  api.post(`/admin/licences/${post_licence_id}/modules`, body);

// ── Chargily Payment ───────────────────────────────────────────────────────────
export const initiatePayment = (pharmacie_id, body) =>
  api.post(`/admin/pharmacies/${pharmacie_id}/checkout`, body);

// ── Pharmacy onboarding (public — no token) ───────────────────────────────────
export const registerPharmacie = (formData) =>
  api.post("/admin/pharmacies/register", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

// ── Module 2: Billing ──────────────────────────────────────────────────────────
export const getBillingDashboard = () => api.get("/admin/billing");
export const getPricingConfig = () => api.get("/admin/billing/pricing");
/** X XWP-69 — a new version of a platform price (the user seat): { cle, montant_da } (decimal string). */
export const updatePricingConfig = (body) => api.put("/admin/billing/pricing", body);
/** X XWP-69 — the versions of a platform price, most recent first. */
export const getPricingHistory = (cle, limit = 10) =>
  api.get("/admin/billing/pricing/historique", { params: { cle, limit } });

// ── Module 3: CNAS DB ──────────────────────────────────────────────────────────
export const listMedicaments = (p) => api.get("/admin/cnas", { params: p });
export const getCNASStats = () => api.get("/admin/cnas/stats");
export const createMedicament = (body) => api.post("/admin/cnas", body);
export const updateMedicament = (id, b) => api.patch(`/admin/cnas/${id}`, b);
export const deleteMedicament = (id) => api.delete(`/admin/cnas/${id}`);
export const importCNAS = (body) => api.post("/admin/cnas/import", body);
export const diffCNAS = (body) => api.post("/admin/cnas/diff", body);

// ── Module 4: Monitoring ───────────────────────────────────────────────────────
export const getSystemHealth = () => api.get("/admin/monitor/health");
export const getErrorMetrics = () => api.get("/admin/monitor/errors");
export const listAuditLogsAdmin = (p) =>
  api.get("/admin/monitor/audit", { params: p });
export const detectSuspicious = () =>
  api.get("/admin/monitor/audit/suspicious");

// ── Module 5: Users & Security ────────────────────────────────────────────────
export const listAllUsers = (p) =>
  api.get("/admin/monitor/users", { params: p });
export const deactivateUser = (id) =>
  api.patch(`/admin/monitor/users/${id}/deactivate`);
export const reactivateUser = (id) =>
  api.patch(`/admin/monitor/users/${id}/reactivate`);
export const forcePasswordReset = (id) =>
  api.post(`/admin/monitor/users/${id}/reset-password`);
export const revokeUserSessions = (id) =>
  api.post(`/admin/monitor/users/${id}/revoke-sessions`);
export const getSecurityLog = (p) =>
  api.get("/admin/monitor/security", { params: p });
export const getFailedLoginStats = (p) =>
  api.get("/admin/monitor/security/failed-logins", { params: p });

// ── Module 6: Admin Notifications ─────────────────────────────────────────────
export const listAdminNotifs = (p) =>
  api.get("/comms/admin-notifs", { params: p });
export const getAdminNotifCount = () => api.get("/comms/admin-notifs/count");
export const markAdminNotifRead = (id) =>
  api.post(`/comms/admin-notifs/${id}/read`);
export const markAllAdminNotifsRead = () =>
  api.post("/comms/admin-notifs/read-all");
export const archiveAdminNotif = (id) =>
  api.post(`/comms/admin-notifs/${id}/archive`);
export const archiveAllAdminRead = () =>
  api.post("/comms/admin-notifs/archive-read");

// ── Module 6: Broadcasts ───────────────────────────────────────────────────────
export const listBroadcasts = (p) =>
  api.get("/comms/broadcasts", { params: p });
export const createBroadcast = (body) => api.post("/comms/broadcasts", body);
export const getBroadcast = (id) => api.get(`/comms/broadcasts/${id}`);
export const updateBroadcast = (id, b) =>
  api.patch(`/comms/broadcasts/${id}`, b);
export const deleteBroadcast = (id) => api.delete(`/comms/broadcasts/${id}`);
export const previewBroadcast = (id) =>
  api.get(`/comms/broadcasts/${id}/preview`);
export const sendBroadcast = (id) => api.post(`/comms/broadcasts/${id}/send`);

// ── Module 6: Tickets (Admin side) ────────────────────────────────────────────
export const listAdminTickets = (p) => api.get("/comms/tickets", { params: p });
export const getAdminTicketStats = () => api.get("/comms/tickets/stats");
// The ticket id comes from the URL (`?ticket=<id>`, XWP-63): always encoded into the path.
const ticketPath = (id) => `/comms/tickets/${encodeURIComponent(String(id))}`;
export const getAdminTicket = (id) => api.get(ticketPath(id));
export const adminReply = (id, b) => api.post(`${ticketPath(id)}/reply`, b);
export const updateTicketStatut = (id, b) =>
  api.patch(`${ticketPath(id)}/statut`, b);
export const adminMarkTicketRead = (id) =>
  api.post(`${ticketPath(id)}/mark-read`);

// ── Fournisseur ERP Module 08: Abonnements fournisseurs (ADMIN_PHARMAKON) ─────
// PARALLEL BUILD — coded against the /api/v1/admin/abonnements* contract
// (Module 08 Implementation_Plan §6.2; backend built in parallel). The
// AbonnementsFournisseurs page degrades gracefully on 404 (empty-states) until
// the routes land — the M06/M07 pattern.
export const getAbonnementMetrics = (p) =>
  api.get("/admin/abonnements/metrics", { params: p });
export const listAbonnementsFournisseurs = (p) =>
  api.get("/admin/abonnements", { params: p });
export const listAbonnementPlans = () => api.get("/admin/abonnements/plans");
export const upsertAbonnementPlan = (body) =>
  api.post("/admin/abonnements/plans", body);
// « Publier le catalogue » — bumps the price-book version (B2). Contract
// assumption: a dedicated publish action; reconcile with POST /plans (upsert
// bumps version) at integration.
export const publierAbonnementCatalogue = (body = {}) =>
  api.post("/admin/abonnements/catalogue/publier", body);
export const listAbonnementPromos = (p) =>
  api.get("/admin/abonnements/promos", { params: p });
export const createAbonnementPromo = (body) =>
  api.post("/admin/abonnements/promos", body);
export const updateAbonnementPromo = (id, body) =>
  api.patch(`/admin/abonnements/promos/${id}`, body);
export const getAbonnementCohortes = (p) =>
  api.get("/admin/abonnements/cohortes", { params: p });
export const getAbonnementParrainages = (p) =>
  api.get("/admin/abonnements/parrainages", { params: p });
